/**
 * Performance test files: images and other files a performance project's scripts upload (multipart file fields, or a
 * whole request body). Stored in artifact storage (PerfFile); a run's load agents download the files it uses, check
 * them against their SHA-256 and keep one copy per agent. A file field names a file ("cat.png") or a template that names
 * one per journey ("{{IMAGE}}", a data file column). docs/architecture.md "Performance test files".
 */
import { isFilePart, type PerfScript, type Step } from './perf.js';

/** Per file, per project, and all files of a project together. */
export const PERF_FILE_LIMITS = { bytes: 10 * 1024 * 1024, files: 200, totalBytes: 1024 * 1024 * 1024 } as const;

/** Load agents older than this can't send files, so runs that use one never go to them. */
export const LOAD_AGENT_FILES_MIN_VERSION = '1.3.0';
/** Load agents older than this have no files folder (`--files-dir`). */
export const LOAD_AGENT_FOLDER_MIN_VERSION = '1.4.0';

/**
 * The key a run's file is known by in the engine: a project test file by its lower-case name, a file of the agent's
 * folder as `agent:` + its path in the folder (as written: folders may tell case apart).
 */
export const fileKey = (source: FileSource, name: string) => (source === 'agent' ? `agent:${name.replace(/\\/g, '/')}` : name.toLowerCase());

/**
 * Why `rel` can't name a file in the agent's files folder, or null: only plain paths inside it (photos/0001.jpg), never
 * an absolute path, a drive, `..` or `.` parts. The agent checks again on its disk (links that lead out of the folder).
 */
export function agentPathProblem(rel: string): string | null {
  const p = rel.replace(/\\/g, '/');
  if (!p.trim()) return 'an empty path';
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is refused
  if (/[\u0000-\u001f]/.test(p)) return `${rel} holds control characters`;
  if (p.startsWith('/') || /^[a-zA-Z]:/.test(p) || p.startsWith('~')) return `${rel} is not a path inside the agent's files folder (write it relative to the folder, like photos/0001.jpg)`;
  if (p.split('/').some((part) => part === '..' || part === '.' || part === '')) return `${rel} may not hold "..", "." or empty parts`;
  return null;
}

export const perfFileStorageKey = (projectId: string, uuid: string) => `perf-files/${projectId}/${uuid}`;

/**
 * Why `name` can't be a test file's name, or null. Names are how scripts and data files refer to files, so they are
 * kept plain: no folders, no control characters, no {{ }}.
 */
export function perfFileNameProblem(name: string): string | null {
  if (!name.trim()) return 'Give the file a name, for example cat.png.';
  if (name !== name.trim()) return 'The name can’t start or end with a space.';
  if (name.length > 200) return 'The name can be at most 200 characters.';
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is refused
  if (/[\\/\u0000-\u001f\u007f]/.test(name)) return 'The name can’t hold / or \\ or control characters.';
  if (/\{\{|\}\}/.test(name)) return 'The name can’t hold {{ or }}.';
  if (name === '.' || name === '..') return 'Choose another name.';
  return null;
}

/** A test file as the API lists it. */
export interface PerfFileView {
  id: string;
  name: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  /** Scripts of the project whose file fields name it (fixed names only; {{templates}} are resolved when a run starts). */
  usedBy: { id: string; name: string }[];
}

/** A test file of a run, as the load agent gets it (downloaded from 1QA and checked against `sha256`). */
export interface RunFile {
  id: string;
  name: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
}

export type FileSource = 'project' | 'agent';

/** Where a script sends a file: the step, the field (null: the whole body), the reference as written and its source. */
export interface FileUse {
  stepId: string;
  stepName: string;
  field: string | null;
  ref: string;
  source: FileSource;
}

const TEMPLATE = /\{\{[^{}]*\}\}/;

/** True when a reference holds a {{template}} (a name chosen per journey) rather than one fixed file name. */
export const isTemplatedRef = (ref: string) => TEMPLATE.test(ref);

function stepFileUses(step: Step): FileUse[] {
  if (step.kind !== 'http' || step.skip) return [];
  const b = step.body;
  if (b.kind === 'file') return [{ stepId: step.id, stepName: step.name, field: null, ref: b.file, source: b.source ?? 'project' }];
  if (b.kind === 'multipart') return b.parts.filter(isFilePart).map((p) => ({ stepId: step.id, stepName: step.name, field: p.name, ref: p.file, source: p.source ?? 'project' }));
  return [];
}

/** Every file the script sends (skipped steps left out), in request order. */
export const scriptFileUses = (script: PerfScript): FileUse[] => script.transactions.flatMap((t) => t.steps.flatMap(stepFileUses));

/** The fixed names of project test files a script sends (no templates, not empty; not the agent folder's). */
export const fixedFileNames = (script: PerfScript): string[] => [...new Set(scriptFileUses(script).flatMap((u) => (u.source === 'project' && u.ref && !isTemplatedRef(u.ref) ? [u.ref] : [])))];

/** True when a script sends files from the load agent's folder (such runs need agents with `--files-dir`). */
export const usesAgentFiles = (script: PerfScript): boolean => scriptFileUses(script).some((u) => u.source === 'agent');

/**
 * Every file a scenario sends from the agent's folder, as paths in the folder: fixed ones as written, templated ones
 * rendered with each data file row (`columns` / `rows`) or once with the test data. Problems in plain words.
 */
export function agentFilePaths(script: PerfScript, columns: string[], rows: string[][], data: Record<string, string>): { paths: Set<string> } | { problem: string } {
  const paths = new Set<string>();
  for (const u of scriptFileUses(script)) {
    if (u.source !== 'agent') continue;
    const where = `${u.field ? `field "${u.field}" of ` : ''}"${u.stepName}"`;
    if (!u.ref.trim()) return { problem: `No file is chosen for ${where}. Choose one in the script editor.` };
    for (const row of isTemplatedRef(u.ref) && rows.length ? rows : [[] as string[]]) {
      const rel = renderRef(u.ref, (k) => {
        const i = columns.indexOf(k);
        return i !== -1 ? row[i] : data[k];
      }).trim();
      if (isTemplatedRef(rel)) return { problem: `${u.ref} in ${where} names no file: give it a value in the data file or the test data.` };
      const problem = agentPathProblem(rel);
      if (problem) return { problem: `${where}: ${problem}.` };
      paths.add(rel.replace(/\\/g, '/'));
    }
  }
  return { paths };
}

/**
 * A template string with values filled in, the way the engine does for a file reference: `{{NAME}}` and
 * `{{NAME|written}}` (written text when there is no value). Names without a value stay as written (`{{NAME}}`).
 */
export function renderRef(ref: string, value: (name: string) => string | undefined): string {
  return ref.replace(/\{\{\s*([^{}|]+?)\s*(?:\|([^{}]*))?\}\}/g, (whole, raw: string, written: string | undefined) => {
    const name = raw.replace(/:(url|form|json)$/, '');
    const v = value(name);
    if (v !== undefined) return v;
    if (written !== undefined) {
      try {
        return JSON.parse(`"${written}"`) as string;
      } catch {
        return written;
      }
    }
    return whole;
  });
}

/** "3.4 MB" / "820 KB" / "12 B". */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}
